import {
  DayOfWeek,
  MinRetentionRuleFrequencyEnum,
  StandardBackupScheduleFrequency,
  type BackupSchedule,
  type StandardBackupScheduleConfig,
} from '@repo/api-gw-sdk';

import {
  createDayJsTime,
  toDays,
  type DurationFragment,
} from '@/utils/dateTime';
import { dayjs } from '@/utils/dayjs';

import { MIN_WINDOW_HOURS } from './controls/hoursSelector';

export interface Frequency {
  windows: string;
  text: string;
  retentionUnit: DurationFragment;
  defaultRetentionInDays: number;
  minimumRetentionInDays: number;
  timesPerDay?: number;
}

export const twiceADayFrequency: Frequency = {
  windows: '0 0,12 * * *',
  text: 'Every 12 hours',
  retentionUnit: 'days',
  defaultRetentionInDays: toDays('weeks', 2),
  minimumRetentionInDays: 5,
  timesPerDay: 2,
};

export const threeTimesADayFrequency: Frequency = {
  windows: '0 0,8,16 * * *',
  text: 'Every 8 hours',
  retentionUnit: 'days',
  defaultRetentionInDays: toDays('weeks', 2),
  minimumRetentionInDays: 5,
  timesPerDay: 3,
};

export const fourTimesADayFrequency: Frequency = {
  windows: '0 0,6,12,18 * * *',
  text: 'Every 6 hours',
  retentionUnit: 'days',
  defaultRetentionInDays: toDays('weeks', 2),
  minimumRetentionInDays: 5,
  timesPerDay: 4,
};

export const sixTimesADayFrequency: Frequency = {
  windows: '0 0,4,8,12,16,20 * * *',
  text: 'Every 4 hours',
  retentionUnit: 'days',
  defaultRetentionInDays: toDays('weeks', 1),
  minimumRetentionInDays: 5,
  timesPerDay: 6,
};

const eightTimesADayFrequency: Frequency = {
  windows: '0 0,3,6,9,12,15,18,21 * * *',
  text: 'Every 3 hours',
  retentionUnit: 'days',
  defaultRetentionInDays: toDays('weeks', 1),
  minimumRetentionInDays: 5,
  timesPerDay: 8,
};

export const twelveTimesADayFrequency: Frequency = {
  windows: '0 0,2,4,6,8,10,12,14,16,18,20,22 * * *',
  text: 'Every 2 hours',
  retentionUnit: 'days',
  defaultRetentionInDays: toDays('weeks', 1),
  minimumRetentionInDays: 5,
  timesPerDay: 12,
};

export const hourlyFrequency: Frequency = {
  windows: '0 * * * *',
  text: 'Every 1 hour',
  retentionUnit: 'days',
  defaultRetentionInDays: 2,
  minimumRetentionInDays: 1,
  timesPerDay: 24,
};

export const thirtyMinutesFrequency: Frequency = {
  windows: '*/30 * * * *',
  text: 'Every 30 minutes',
  retentionUnit: 'days',
  defaultRetentionInDays: 2,
  minimumRetentionInDays: 1,
  timesPerDay: 48,
};

const fifteenMinutesFrequency: Frequency = {
  windows: '*/15 * * * *',
  text: 'Every 15 minutes',
  retentionUnit: 'days',
  defaultRetentionInDays: 1,
  minimumRetentionInDays: 1,
  timesPerDay: 96,
};

export const dailyFrequency: Frequency = {
  windows: '0 0 * * *',
  text: 'Daily',
  retentionUnit: 'days',
  defaultRetentionInDays: 30,
  minimumRetentionInDays: 5,
};

export const weeklyFrequency: Frequency = {
  windows: '0 0 * * 0',
  text: 'Weekly',
  retentionUnit: 'weeks',
  defaultRetentionInDays: toDays('months', 2),
  minimumRetentionInDays: toDays('weeks', 2),
};

export const monthlyFrequency: Frequency = {
  windows: '0 0 1 * *',
  text: 'Monthly',
  retentionUnit: 'months',
  defaultRetentionInDays: toDays('months', 6),
  minimumRetentionInDays: toDays('months', 2),
};

export const annuallyFrequency: Frequency = {
  windows: '0 0 1 1 *',
  text: 'Annually',
  retentionUnit: 'years',
  defaultRetentionInDays: toDays('years', 2),
  minimumRetentionInDays: toDays('years', 2),
};

const frequencyOptions: Frequency[] = [
  twiceADayFrequency,
  threeTimesADayFrequency,
  fourTimesADayFrequency,
  sixTimesADayFrequency,
  eightTimesADayFrequency,
  twelveTimesADayFrequency,
  hourlyFrequency,
  thirtyMinutesFrequency,
  fifteenMinutesFrequency,
  dailyFrequency,
  weeklyFrequency,
  monthlyFrequency,
  annuallyFrequency,
];

export const highFrequencyOptions: Frequency[] = [
  sixTimesADayFrequency,
  eightTimesADayFrequency,
  twelveTimesADayFrequency,
  hourlyFrequency,
  thirtyMinutesFrequency,
  fifteenMinutesFrequency,
];

export const lessThanDayOptions: Frequency[] = [
  twiceADayFrequency,
  threeTimesADayFrequency,
  fourTimesADayFrequency,
  sixTimesADayFrequency,
  eightTimesADayFrequency,
  twelveTimesADayFrequency,
  hourlyFrequency,
  thirtyMinutesFrequency,
  fifteenMinutesFrequency,
];

export const pitrFrequencyOptions: Frequency[] = [
  hourlyFrequency,
  thirtyMinutesFrequency,
  fifteenMinutesFrequency,
];

export const getFrequencyByWindow = (window: string) => {
  const frequency = frequencyOptions.find((x) => x.windows === window);

  if (frequency) {
    return frequency;
  }

  const { minutes, hours, dayOfMonth, month, dayOfWeek } =
    deconstructCron(window);

  const isSimpleValue = (field: string | undefined) => {
    if (!field) {
      return false;
    }
    return field !== '*' && !field.includes('*/') && !field.includes(',');
  };

  if (
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '*' &&
    hours?.startsWith('*/')
  ) {
    const hourStep = parseInt(hours.substring(2));

    const stepToFrequency: Record<number, Frequency> = {
      1: hourlyFrequency,
      2: twelveTimesADayFrequency,
      3: eightTimesADayFrequency,
      4: sixTimesADayFrequency,
      6: fourTimesADayFrequency,
      8: threeTimesADayFrequency,
      12: twiceADayFrequency,
    };

    if (stepToFrequency[hourStep]) {
      return stepToFrequency[hourStep];
    }
  }

  if (
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '*' &&
    hours === '*' &&
    minutes?.startsWith('*/')
  ) {
    const minuteStep = parseInt(minutes.substring(2));

    if (minuteStep === 15) {
      return fifteenMinutesFrequency;
    }
    if (minuteStep === 30) {
      return thirtyMinutesFrequency;
    }
  }

  if (
    isSimpleValue(minutes) &&
    isSimpleValue(hours) &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek === '*'
  ) {
    return dailyFrequency;
  }

  if (
    isSimpleValue(minutes) &&
    isSimpleValue(hours) &&
    dayOfMonth === '*' &&
    month === '*' &&
    dayOfWeek !== '*'
  ) {
    return weeklyFrequency;
  }

  if (
    isSimpleValue(minutes) &&
    isSimpleValue(hours) &&
    dayOfMonth !== '*' &&
    month === '*' &&
    dayOfWeek === '*'
  ) {
    return monthlyFrequency;
  }

  if (
    isSimpleValue(minutes) &&
    isSimpleValue(hours) &&
    dayOfMonth !== '*' &&
    month !== '*' &&
    dayOfWeek === '*'
  ) {
    return annuallyFrequency;
  }
};

export const getFrequencyDisplayName = (window: string) => {
  const frequency = getFrequencyByWindow(window);
  if (!frequency || frequency === dailyFrequency) {
    return '';
  }

  const { dayOfMonth, dayOfWeek } = deconstructCron(window);

  if (frequency === weeklyFrequency && dayOfWeek) {
    return dayOfWeek
      .split(',')
      .map((day) => getDayOfWeekDisplay(parseInt(day)))
      .join(', ');
  }

  if (frequency === monthlyFrequency && dayOfMonth) {
    return dayOfMonth
      .split(',')
      .map((day) => getDayOfMonthDisplay(day))
      .join(', ');
  }

  if (frequency === annuallyFrequency) {
    return collectDaysAndMonths(window)
      .map((x) => getDayOfYearDisplay(x.day, x.month))
      .join(', ');
  }

  return null;
};

export const getDayOfWeekDisplay = (day: number) =>
  dayjs().day(day).format('dddd');

export const getDayOfMonthDisplay = (day: string) =>
  dayjs().date(parseInt(day)).format('Do');

export const getDayOfYearDisplay = (day: number, month: number) =>
  dayjs()
    .date(day)
    .month(month - 1)
    .format('MMM D');

export const getHoursDisplay = (hours: string, minutes: string) => {
  let _hours: string;
  if (hours === '*') {
    _hours = Array.from({ length: 24 }, (_, i) => i).join(',');
  } else if (hours.startsWith('*/')) {
    const step = parseInt(hours.substring(2));
    _hours = Array.from(
      { length: Math.floor(24 / step) },
      (_, i) => i * step
    ).join(',');
  } else {
    _hours = hours;
  }

  let _minutes: string[];
  if (!minutes.includes('*')) {
    _minutes = [minutes];
  } else if (minutes === '*') {
    _minutes = ['0'];
  } else if (minutes.startsWith('*/')) {
    const step = parseInt(minutes.substring(2));
    _minutes = Array.from({ length: Math.floor(60 / step) }, (_, i) =>
      String(i * step)
    );
  } else {
    _minutes = ['0'];
  }

  const windows: string[] = [];
  _hours
    .split(',')
    .forEach((x) =>
      _minutes.forEach((minute) =>
        windows.push(
          createDayJsTime(parseInt(x), parseInt(minute)).format('HH:mm')
        )
      )
    );

  if (windows.length === 1) {
    return windows[0];
  }

  if (windows.length <= 4) {
    return `${windows.slice(0, -1).join(', ')}, and ${windows[windows.length - 1]}`;
  }

  const first3 = windows.slice(0, 3);
  const numOfRest = windows.length - 3;
  return `${first3.join(', ')}, and ${numOfRest} more`;
};

export const collectDaysAndMonths = (window: string) => {
  const { dayOfMonth, month } = deconstructCron(window);

  if (!dayOfMonth || !month) {
    return [];
  }

  return [dayOfMonth.split(','), month.split(',')]
    .reduce<number[][]>((agg, arr) => {
      arr.forEach((x, index) => {
        agg[index] = agg[index] || [];
        agg[index].push(parseInt(x));
      });
      return agg;
    }, [])
    .map(([day, month]) => ({ day, month }));
};

export const deconstructCron = (cron: string) => {
  const [minutes, hours, dayOfMonth, month, dayOfWeek] = cron.split(' ');

  return {
    minutes,
    hours,
    dayOfMonth,
    month,
    dayOfWeek,
  };
};

const toTimeOfDay = (hour: string, minute: string) => ({
  hour: Number(hour) || 0,
  minute: Number(minute) || 0,
});

const dayOfWeekByCronValue: Record<string, DayOfWeek> = {
  '0': DayOfWeek.Sun,
  '1': DayOfWeek.Mon,
  '2': DayOfWeek.Tue,
  '3': DayOfWeek.Wed,
  '4': DayOfWeek.Thu,
  '5': DayOfWeek.Fri,
  '6': DayOfWeek.Sat,
  '7': DayOfWeek.Sun,
};

const toDaysOfWeek = (value: string): DayOfWeek[] =>
  value
    .split(',')
    .map((day) => dayOfWeekByCronValue[day])
    .filter((day) => !!day);

const toCronDayOfWeek = (day: DayOfWeek): number =>
  day === DayOfWeek.Sun
    ? 0
    : day === DayOfWeek.Mon
      ? 1
      : day === DayOfWeek.Tue
        ? 2
        : day === DayOfWeek.Wed
          ? 3
          : day === DayOfWeek.Thu
            ? 4
            : day === DayOfWeek.Fri
              ? 5
              : 6;

const firstValue = (value: string) => value.split(',')[0] || '0';

export const standardScheduleConfigToBackupSchedule = (
  config: StandardBackupScheduleConfig | null | undefined,
  base: BackupSchedule
): BackupSchedule => {
  if (!config) {
    return base;
  }

  if (
    config.frequency ===
      StandardBackupScheduleFrequency.STANDARD_BACKUP_SCHEDULE_INTERVAL &&
    config.intervalConfig?.intervalHours
  ) {
    const intervalToFrequency: Record<number, Frequency> = {
      6: fourTimesADayFrequency,
      8: threeTimesADayFrequency,
      12: twiceADayFrequency,
    };
    const frequency = intervalToFrequency[config.intervalConfig.intervalHours];
    return {
      ...base,
      windows: frequency?.windows ?? base.windows,
      duration: frequency ? getWindowHoursDuration(frequency) * 60 : 240,
    };
  }

  if (
    config.frequency ===
      StandardBackupScheduleFrequency.STANDARD_BACKUP_SCHEDULE_WEEKLY &&
    config.weeklyConfig
  ) {
    const daysOfWeek = Array.from(config.weeklyConfig.daysOfWeek ?? []);
    if (daysOfWeek.length) {
      const dayIndexes = daysOfWeek.map(toCronDayOfWeek);
      const time = config.weeklyConfig.timeOfDay;
      return {
        ...base,
        windows: `${time?.minute ?? 0} ${time?.hour ?? 0} * * ${dayIndexes.join(',')}`,
        duration: config.weeklyConfig.startWindowMinutes ?? 240,
      };
    }
  }

  if (
    config.frequency ===
      StandardBackupScheduleFrequency.STANDARD_BACKUP_SCHEDULE_MONTHLY &&
    config.monthlyConfig
  ) {
    const daysOfMonth = Array.from(config.monthlyConfig.daysOfMonth ?? []);
    if (daysOfMonth.length) {
      const time = config.monthlyConfig.timeOfDay;
      return {
        ...base,
        windows: `${time?.minute ?? 0} ${time?.hour ?? 0} ${daysOfMonth.join(',')} * *`,
        duration: config.monthlyConfig.startWindowMinutes ?? 240,
      };
    }
  }

  if (
    config.frequency ===
      StandardBackupScheduleFrequency.STANDARD_BACKUP_SCHEDULE_ANNUALLY &&
    config.annuallyConfig
  ) {
    const time = config.annuallyConfig?.timeOfDay;
    const timeOfYear = config.annuallyConfig?.timeOfYear;
    return {
      ...base,
      windows: `${time?.minute ?? 0} ${time?.hour ?? 0} ${timeOfYear?.dayOfMonth ?? 1} ${timeOfYear?.month ?? 1} *`,
      duration: config.annuallyConfig?.startWindowMinutes ?? 240,
    };
  }

  const time = config.dailyConfig?.timeOfDay;
  return {
    ...base,
    windows: `${time?.minute ?? 0} ${time?.hour ?? 0} * * *`,
    duration: config.dailyConfig?.startWindowMinutes ?? 240,
  };
};

export const backupScheduleToStandardScheduleConfig = (
  schedule: BackupSchedule
): StandardBackupScheduleConfig => {
  const frequency = getFrequencyByWindow(schedule.windows);
  if (frequency === twiceADayFrequency) {
    return {
      frequency:
        StandardBackupScheduleFrequency.STANDARD_BACKUP_SCHEDULE_INTERVAL,
      intervalConfig: { intervalHours: 12 },
    };
  }
  if (frequency === threeTimesADayFrequency) {
    return {
      frequency:
        StandardBackupScheduleFrequency.STANDARD_BACKUP_SCHEDULE_INTERVAL,
      intervalConfig: { intervalHours: 8 },
    };
  }
  if (frequency === fourTimesADayFrequency) {
    return {
      frequency:
        StandardBackupScheduleFrequency.STANDARD_BACKUP_SCHEDULE_INTERVAL,
      intervalConfig: { intervalHours: 6 },
    };
  }

  const { minutes, hours, dayOfMonth, month, dayOfWeek } = deconstructCron(
    schedule.windows
  );
  const timeOfDay = toTimeOfDay(firstValue(hours), firstValue(minutes));
  const startWindowMinutes = schedule.duration || 240;

  if (frequency === weeklyFrequency) {
    return {
      frequency:
        StandardBackupScheduleFrequency.STANDARD_BACKUP_SCHEDULE_WEEKLY,
      weeklyConfig: {
        daysOfWeek: new Set(toDaysOfWeek(dayOfWeek)),
        timeOfDay,
        startWindowMinutes,
      },
    };
  }
  if (frequency === monthlyFrequency) {
    return {
      frequency:
        StandardBackupScheduleFrequency.STANDARD_BACKUP_SCHEDULE_MONTHLY,
      monthlyConfig: {
        daysOfMonth: new Set(
          dayOfMonth.split(',').map((day) => Number(day) || 1)
        ),
        timeOfDay,
        startWindowMinutes,
      },
    };
  }
  if (frequency === annuallyFrequency) {
    return {
      frequency:
        StandardBackupScheduleFrequency.STANDARD_BACKUP_SCHEDULE_ANNUALLY,
      annuallyConfig: {
        timeOfDay,
        timeOfYear: {
          month: Number(month) || 1,
          dayOfMonth: Number(dayOfMonth) || 1,
        },
        startWindowMinutes,
      },
    };
  }

  return {
    frequency: StandardBackupScheduleFrequency.STANDARD_BACKUP_SCHEDULE_DAILY,
    dailyConfig: {
      timeOfDay,
      startWindowMinutes,
    },
  };
};

type ControlFrequency = Frequency & {
  type: MinRetentionRuleFrequencyEnum;
};

export const controlsFrequencies: ControlFrequency[] = [
  {
    ...dailyFrequency,
    type: MinRetentionRuleFrequencyEnum.Daily,
  },
  {
    ...weeklyFrequency,
    type: MinRetentionRuleFrequencyEnum.Weekly,
  },
  {
    ...monthlyFrequency,
    type: MinRetentionRuleFrequencyEnum.Monthly,
  },
  {
    ...annuallyFrequency,
    type: MinRetentionRuleFrequencyEnum.Annual,
  },
];

export const controlsFrequenciesMapping: Record<
  MinRetentionRuleFrequencyEnum,
  ControlFrequency
> = Object.fromEntries(
  controlsFrequencies.map((freq) => [freq.type, freq])
) as Record<MinRetentionRuleFrequencyEnum, ControlFrequency>;

export const getWindowHoursDuration = (frequency: Frequency) => {
  const highFrequency = lessThanDayOptions.find(
    (x) => x.windows === frequency.windows
  );

  if (!highFrequency?.timesPerDay) {
    return MIN_WINDOW_HOURS;
  }

  return Math.max(24 / highFrequency.timesPerDay - 2, 0); // 10, 6, 4, 2, 1, 0
};

export const getWindowDurationDisplay = (durationInHours: number) => {
  const durationDisplay =
    durationInHours === 0
      ? '5 min'
      : durationInHours === 1
        ? 'one hour'
        : `${durationInHours} hours`;
  return durationDisplay;
};
